import { useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import {
  getReviewItem,
  listReviewItems,
  recordDecision,
} from "@/api/reviewItems";
import type {
  DecisionVerb,
  ReviewItem,
  ReviewDecision,
} from "@/api/types";
import { useToast } from "@/components/toast";
import { getUsername } from "@/auth/roles";
import {
  decisionLabel,
  itemTypeLabel,
  langTagLabel,
  reviewContextHint,
  reviewQueueLabel,
  reviewReasonLabel,
  ruEnd,
  statusLabel,
} from "./labels";
import {
  LlmSuggestionPanel,
  type AppliedAlternativeSelection,
} from "./components/LlmSuggestionPanel";
import {
  fieldOverridesForItem,
  type PayloadShape,
} from "./components/recordForms/shared";
import { RecordFormSwitch } from "./components/recordForms/RecordFormSwitch";

const HOTKEYS: Record<string, DecisionVerb> = {
  a: "approve",
  e: "edit_and_approve",
  r: "reject",
  n: "needs_second_review",
  s: "skip",
};

export default function AdminReviewFocus() {
  const { stableKey = "" } = useParams<{ stableKey: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [tab, setTab] = useState<"record" | "history" | "evidence">("record");
  // Edit mode + черновик правок — собирается ключами payload.* + item.*.
  // При Confirm уходит в recordDecision как after_payload.
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  // Когда reviewer применил альтернативу LLM — запоминаем suggestion_id,
  // чтобы вписать его в after_payload при Confirm. Очищается на success/cancel.
  const [pendingSuggestion, setPendingSuggestion] = useState<{
    suggestion_id: string;
    applied_alternative_index: number;
  } | null>(null);

  // Сбрасываем edit-mode и draft при смене item'а.
  useEffect(() => {
    setEditMode(false);
    setDraft({});
    setPendingSuggestion(null);
  }, [stableKey]);

  const itemQuery = useQuery({
    queryKey: ["review-item", stableKey],
    queryFn: async () => {
      const r = await getReviewItem(stableKey);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    enabled: Boolean(stableKey),
  });

  const queueQuery = useQuery({
    queryKey: ["review-items", "focus-queue", params.toString()],
    queryFn: async () => {
      const r = await listReviewItems({
        status: params.get("status") ?? "open",
        item_type: params.get("item_type") ?? undefined,
        domain: params.get("domain") ?? undefined,
        limit: 200,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
  });

  const queue = queueQuery.data?.items ?? [];
  const idx = queue.findIndex(
    (it) =>
      it.stable_key === stableKey || it.review_item_id === stableKey,
  );
  const prev = idx > 0 ? queue[idx - 1] : null;
  const next = idx >= 0 && idx < queue.length - 1 ? queue[idx + 1] : null;

  const reviewer =
    (auth.user?.profile?.preferred_username as string | undefined) ??
    (auth.user?.profile?.email as string | undefined) ??
    "anonymous";

  const decideMutation = useMutation({
    mutationFn: async (input: {
      decision: DecisionVerb;
      after_payload?: Record<string, unknown>;
    }) => {
      const r = await recordDecision(stableKey, {
        decision: input.decision,
        reviewer,
        reviewer_note: note || undefined,
        after_payload: input.after_payload,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: () => {
      toast("Применено", "success");
      setNote("");
      setNoteOpen(false);
      setEditMode(false);
      setDraft({});
      setPendingSuggestion(null);
      void qc.invalidateQueries({ queryKey: ["review-items"] });
      void qc.invalidateQueries({ queryKey: ["review-item", stableKey] });
      void qc.invalidateQueries({ queryKey: ["llm-suggestions", stableKey] });
      if (autoAdvance && next) {
        navigate(focusUrl(next, params));
      }
    },
    onError: (err: Error) => toast(`Не применено: ${err.message}`, "error"),
  });

  /** Принять решение. Для `edit_and_approve` без edit-mode — переводим
   *  форму в редактируемый режим (правки появляются inline), при повторном
   *  клике по той же кнопке отправляем decision с собранным after_payload. */
  const handleDecision = (decision: DecisionVerb): void => {
    if (decision === "edit_and_approve") {
      if (!editMode) {
        setEditMode(true);
        toast("Поля разблокированы — нажми «Подтвердить» для отправки", "info");
        return;
      }
      // editMode=true → confirm: собираем after_payload из draft + LLM-attribution.
      const baseDraft: Record<string, unknown> =
        Object.keys(draft).length > 0 ? { ...draft } : {};
      if (pendingSuggestion) {
        baseDraft.llm_suggestion_id = pendingSuggestion.suggestion_id;
        baseDraft.applied_alternative_index =
          pendingSuggestion.applied_alternative_index;
      }
      decideMutation.mutate({
        decision: "edit_and_approve",
        after_payload:
          Object.keys(baseDraft).length > 0 ? baseDraft : undefined,
      });
      return;
    }
    // Любое другое решение в edit mode сбрасывает edit без применения правок
    if (editMode) {
      setEditMode(false);
      setDraft({});
      setPendingSuggestion(null);
    }
    decideMutation.mutate({ decision });
  };

  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (
        ev.target instanceof HTMLElement &&
        (ev.target.tagName === "INPUT" || ev.target.tagName === "TEXTAREA")
      ) {
        return;
      }
      if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
      const verb = HOTKEYS[ev.key.toLowerCase()];
      if (verb) {
        ev.preventDefault();
        handleDecision(verb);
        return;
      }
      if (ev.key === "j" && next) {
        ev.preventDefault();
        navigate(focusUrl(next, params));
      }
      if (ev.key === "k" && prev) {
        ev.preventDefault();
        navigate(focusUrl(prev, params));
      }
      if (ev.key === "Escape") {
        ev.preventDefault();
        if (editMode) {
          setEditMode(false);
          setDraft({});
          setPendingSuggestion(null);
          return;
        }
        navigate(`/admin/review?${params.toString()}`);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, next, prev, navigate, params, draft]);

  if (itemQuery.isLoading) {
    return <div className="mt-empty">Загружаем…</div>;
  }
  if (itemQuery.isError) {
    return (
      <div className="mt-empty mt-empty--error">
        {(itemQuery.error as Error).message}
      </div>
    );
  }
  const detail = itemQuery.data!;
  const item = detail.item;
  // payload в БД — это input snapshot и в него правки reviewer'а НЕ
  // записываются. Свежие правки лежат в review_decisions.after_payload
  // самого недавнего decision'а. Подмерживаем для отображения.
  const latestAfterPayload = pickLatestAfterPayload(detail.decisions);
  const payload = {
    ...((item.payload ?? {}) as PayloadShape),
    ...latestAfterPayload,
  } as PayloadShape;

  // % прогресс по очереди
  const total = queue.length;
  const progressPct =
    total > 0 ? Math.max(0, Math.min(100, ((idx + 1) / total) * 100)) : 0;

  return (
    <>
      {/* PROGRESS STRIP — sticky */}
      <div className="rf-progress">
        <Link to="/admin/review" className="mt-btn mt-btn--sm mt-btn--ghost">
          ‹ К очереди
        </Link>
        <div className="mt-divider--v" />
        <span>
          фильтр:{" "}
          <strong style={{ color: "var(--ink)" }}>
            {params.get("status") ?? "open"}
          </strong>
        </span>
        <span className="mt-muted">·</span>
        <span className="rf-progress__counter">
          {idx >= 0 ? `${idx + 1} / ${total}` : "—"}
        </span>
        <div className="rf-progress__bar">
          <i style={{ width: `${progressPct}%` }} />
        </div>
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
        <span className="mt-text-tight mt-muted">Auto-advance</span>
        <label className="mt-check">
          <input
            type="checkbox"
            checked={autoAdvance}
            onChange={(e) => setAutoAdvance(e.target.checked)}
          />{" "}
          on
        </label>
        <div className="mt-divider--v" />
        <div className="rf-progress__nav">
          <button
            className="mt-icon-btn mt-icon-btn--bordered"
            onClick={() => prev && navigate(focusUrl(prev, params))}
            disabled={!prev}
            title="Предыдущая (k)"
            type="button"
          >
            ‹
          </button>
          <button
            className="mt-icon-btn mt-icon-btn--bordered"
            onClick={() => next && navigate(focusUrl(next, params))}
            disabled={!next}
            title="Следующая (j)"
            type="button"
          >
            ›
          </button>
        </div>
      </div>

      <div className="rf-shell">
        <main className="rf-main">
          <div className="rf-content">
            {/* HEADER */}
            <header className="rf-head">
              <div className="rf-head__row">
                <span className={`mt-itemtype mt-itemtype--${item.item_type}`}>
                  {itemTypeLabel(item.item_type)}
                </span>
                <span className={`mt-badge ${badgeForStatus(item.status)}`}>
                  {statusLabel(item.status)}
                </span>
                {(payload as { reopen_of_term_id?: string })
                  .reopen_of_term_id && (
                  <span
                    className="mt-badge mt-badge--info mt-badge--no-dot"
                    title={
                      "Reopen существующего одобренного термина " +
                      ((payload as { reopen_of_term_id?: string })
                        .reopen_of_term_id ?? "")
                    }
                  >
                    ↻ reopen
                  </span>
                )}
                <span className="mt-toolbar__sep" style={{ flex: 1 }} />
                <span className="mt-text-tight mt-muted mt-mono">
                  {item.stable_key}
                </span>
                <button
                  className="mt-icon-btn mt-icon-btn--bordered"
                  title="Скопировать stable_key"
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(item.stable_key);
                    toast("Скопировано", "success");
                  }}
                >
                  ⧉
                </button>
              </div>
              <h1 className="rf-head__title">
                {item.source_lang && item.source_lang !== "und" && (
                  <span
                    className="mt-review__lang-tag"
                    style={{ fontSize: 11 }}
                  >
                    {langTagLabel(item.source_lang)}
                  </span>
                )}
                <span className="rf-head__abbr">
                  {item.source_text ?? payload.surface ?? "—"}
                </span>
                {item.candidate_text ? (
                  <>
                    <span className="rf-head__sub">→</span>
                    {item.target_lang && item.target_lang !== "und" && (
                      <span
                        className="mt-review__lang-tag"
                        style={{ fontSize: 11 }}
                      >
                        {langTagLabel(item.target_lang)}
                      </span>
                    )}
                    <span style={{ color: "var(--text-soft)" }}>
                      {item.candidate_text}
                    </span>
                  </>
                ) : (
                  <span
                    className="mt-text-tight mt-muted"
                    style={{
                      fontSize: "var(--fs-md)",
                      fontWeight: "var(--fw-regular)",
                      fontStyle: "italic",
                    }}
                  >
                    — перевод не предложен
                  </span>
                )}
              </h1>
              <div className="rf-head__meta">
                {item.domain && (
                  <span>
                    <strong>{item.domain}</strong>
                  </span>
                )}
                {item.source_key && (
                  <>
                    <span>·</span>
                    <span>
                      source:{" "}
                      <span className="mt-mono">{item.source_key}</span>
                    </span>
                  </>
                )}
                {item.confidence != null && (
                  <>
                    <span>·</span>
                    <span>
                      confidence:{" "}
                      <span
                        className="mt-mono"
                        style={{
                          color: "var(--ink)",
                          fontWeight: "var(--fw-semi)",
                        }}
                      >
                        {item.confidence.toFixed(2)}
                      </span>
                    </span>
                  </>
                )}
                {payload.raw_occurrence_count != null && (
                  <>
                    <span>·</span>
                    <span>
                      встречено:{" "}
                      <span className="mt-mono">
                        {payload.raw_occurrence_count.toLocaleString("ru-RU")}
                      </span>
                    </span>
                  </>
                )}
                {payload.review_queue && (
                  <>
                    <span>·</span>
                    <span>
                      очередь:{" "}
                      <strong>{reviewQueueLabel(payload.review_queue)}</strong>
                    </span>
                  </>
                )}
              </div>
            </header>

            {/* CONTEXT BANNER — для специфичных очередей объясняем что это
                за задача (RU-only normalization vs обычный перевод). */}
            {(() => {
              const ctx = reviewContextHint(payload.review_queue);
              if (!ctx) return null;
              return (
                <div
                  className="rf-banner-inline"
                  style={{
                    background: "var(--blue-50)",
                    borderColor: "var(--blue-300, var(--blue-100))",
                    color: "var(--blue-700)",
                  }}
                >
                  <span
                    className="rf-banner-inline__icon"
                    style={{ background: "var(--blue-700)" }}
                  >
                    ?
                  </span>
                  <div style={{ display: "grid", gap: 2 }}>
                    <strong>{ctx.title}</strong>
                    <span
                      className="mt-text-tight"
                      style={{
                        textTransform: "none",
                        letterSpacing: 0,
                        color: "var(--text-soft)",
                      }}
                    >
                      {ctx.hint}
                    </span>
                  </div>
                  {payload.review_queue && (
                    <span className="mt-toolbar__sep" style={{ flex: 1 }} />
                  )}
                  {payload.review_reason && (
                    <span
                      className="mt-mono mt-text-tight"
                      style={{
                        fontSize: "var(--fs-xs)",
                        color: "var(--blue-500)",
                      }}
                    >
                      {reviewReasonLabel(payload.review_reason)}
                    </span>
                  )}
                </div>
              );
            })()}

            {/* AMBIGUOUS BANNER */}
            {payload.is_ambiguous && (
              <div className="rf-banner-inline">
                <span className="rf-banner-inline__icon">±</span>
                <strong>Ambiguous abbreviation.</strong>
                <span>
                  Найдено несколько sense'ов; решение применяется только к
                  выбранному.
                </span>
                <span className="mt-toolbar__sep" style={{ flex: 1 }} />
                {item.source_text && (
                  <Link
                    to={`/admin/abbreviations/by-short/${encodeURIComponent(item.source_text)}`}
                    className="mt-text-tight"
                    style={{
                      color: "var(--amber-700)",
                      fontWeight: "var(--fw-semi)",
                    }}
                  >
                    Sense-grouped view →
                  </Link>
                )}
              </div>
            )}

            {/* TABS */}
            <div className="mt-tabs" style={{ marginBottom: 12 }}>
              <button
                className="mt-tabs__tab"
                aria-selected={tab === "record"}
                onClick={() => setTab("record")}
                type="button"
              >
                Запись
              </button>
              <button
                className="mt-tabs__tab"
                aria-selected={tab === "history"}
                onClick={() => setTab("history")}
                type="button"
              >
                История{" "}
                <span className="mt-badge mt-badge--neutral mt-badge--no-dot">
                  {detail.decisions.length}
                </span>
              </button>
              <button
                className="mt-tabs__tab"
                aria-selected={tab === "evidence"}
                onClick={() => setTab("evidence")}
                type="button"
              >
                Доказательства
              </button>
            </div>

            {tab === "record" && (
              <RecordTab
                item={item}
                payload={payload}
                editMode={editMode}
                draft={draft}
                onChange={(key, value) =>
                  setDraft((prev) => ({ ...prev, [key]: value }))
                }
                onCancelEdit={() => {
                  setEditMode(false);
                  setDraft({});
                  setPendingSuggestion(null);
                }}
                onApplyLlm={(sel) => {
                  const overrides = fieldOverridesForItem(
                    item,
                    sel.alternative.target_text,
                  );
                  setDraft((prev) => ({ ...prev, ...overrides }));
                  setEditMode(true);
                  setPendingSuggestion({
                    suggestion_id: sel.suggestion.suggestion_id,
                    applied_alternative_index: sel.alternative.index,
                  });
                  toast(
                    `Применили A${sel.alternative.index} — нажми «Подтвердить»`,
                    "info",
                  );
                }}
              />
            )}
            {tab === "history" && (
              <HistoryTab decisions={detail.decisions} />
            )}
            {tab === "evidence" && <EvidenceTab payload={payload} />}
          </div>

          {/* DECISION DOCK */}
          <div className="rf-dock">
            {!noteOpen ? (
              <div className="rf-dock__note-toggle">
                <button type="button" onClick={() => setNoteOpen(true)}>
                  + Reviewer note
                </button>
                <span className="mt-muted">
                  — опциональный комментарий для следующего ревьюера
                </span>
              </div>
            ) : (
              <div className="rf-dock__note-row">
                <span
                  className="mt-text-tight mt-muted"
                  style={{ flex: "0 0 auto" }}
                >
                  Reviewer note
                </span>
                <input
                  className="mt-input mt-input--sm"
                  placeholder="comment for next reviewer…"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  autoFocus
                />
                <button
                  className="mt-icon-btn"
                  title="Hide"
                  onClick={() => {
                    setNoteOpen(false);
                    setNote("");
                  }}
                  type="button"
                >
                  ×
                </button>
              </div>
            )}
            <div className="rf-dock__decisions">
              <button
                className="mt-decision mt-decision--approve"
                onClick={() => handleDecision("approve")}
                disabled={decideMutation.isPending}
                type="button"
              >
                <span className="mt-decision__key">A</span>Принять
              </button>
              <button
                className={
                  "mt-decision " +
                  (editMode
                    ? "mt-decision--approve"
                    : "mt-decision--edit")
                }
                onClick={() => handleDecision("edit_and_approve")}
                disabled={decideMutation.isPending}
                type="button"
              >
                <span className="mt-decision__key">E</span>
                {editMode ? "Подтвердить правки" : "Изменить и принять"}
              </button>
              <button
                className="mt-decision mt-decision--reject"
                onClick={() => handleDecision("reject")}
                disabled={decideMutation.isPending}
                type="button"
              >
                <span className="mt-decision__key">R</span>Отклонить
              </button>
              <button
                className="mt-decision"
                onClick={() => handleDecision("needs_second_review")}
                disabled={decideMutation.isPending}
                type="button"
              >
                <span className="mt-decision__key">N</span>На второй ревью
              </button>
              <button
                className="mt-decision"
                onClick={() => handleDecision("skip")}
                disabled={decideMutation.isPending}
                type="button"
              >
                <span className="mt-decision__key">S</span>Пропустить
              </button>
            </div>
          </div>
        </main>

        {/* RIGHT RAIL */}
        <aside className="rf-rail">
          <div className="rf-mini-stat">
            <div>
              <span className="rf-mini-stat__label">очередь</span>
              <span
                className="rf-mini-stat__val"
                style={{ color: "var(--blue-500)" }}
              >
                {total}
              </span>
            </div>
            <div>
              <span className="rf-mini-stat__label">текущий</span>
              <span className="rf-mini-stat__val">
                {idx >= 0 ? idx + 1 : "—"}
              </span>
            </div>
            <div>
              <span className="rf-mini-stat__label">решений</span>
              <span
                className="rf-mini-stat__val"
                style={{ color: "var(--teal-700)" }}
              >
                {detail.decisions.length}
              </span>
            </div>
          </div>

          <div className="rf-rail-tabs">
            <button className="is-active" type="button">
              Лента событий{" "}
              <span className="mt-badge mt-badge--neutral mt-badge--no-dot">
                {detail.decisions.length}
              </span>
            </button>
          </div>
          <Timeline decisions={detail.decisions} username={getUsername(auth.user)} />

          <div className="rf-keyhint">
            <span>
              <span className="mt-kbd">A</span> принять
            </span>
            <span>
              <span className="mt-kbd">E</span> изменить
            </span>
            <span>
              <span className="mt-kbd">R</span> отклонить
            </span>
            <span>
              <span className="mt-kbd">N</span> второй ревью
            </span>
            <span>
              <span className="mt-kbd">S</span> пропустить
            </span>
            <span>
              <span className="mt-kbd">J</span>
              <span className="mt-kbd">K</span> след/пред
            </span>
            <span>
              <span className="mt-kbd">Esc</span> назад
            </span>
          </div>
        </aside>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Tabs / sub-components
// ---------------------------------------------------------------------------

// PayloadShape вынесен в ./components/recordForms/shared.ts.

function RecordTab({
  item,
  payload,
  editMode,
  draft,
  onChange,
  onCancelEdit,
  onApplyLlm,
}: {
  item: ReviewItem;
  payload: PayloadShape;
  editMode: boolean;
  draft: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onCancelEdit: () => void;
  onApplyLlm: (selection: AppliedAlternativeSelection) => void;
}) {
  const disambig = payload.disambiguation ?? [];
  return (
    <>
      <RecordFormSwitch
        item={item}
        payload={payload}
        editMode={editMode}
        draft={draft}
        onChange={onChange}
        onCancelEdit={onCancelEdit}
        onApplyLlm={onApplyLlm}
      />

      {item.review_item_id && (
        <LlmSuggestionPanel
          reviewItemId={item.review_item_id}
          onApply={onApplyLlm}
        />
      )}

      {disambig.length > 0 && (
        <section className="rf-section">
          <div className="rf-section__head">
            <span>Disambiguation — оценка кандидатов</span>
            <span className="mt-toolbar__sep" style={{ flex: 1 }} />
            <span
              className="mt-text-tight mt-muted"
              style={{ textTransform: "none", letterSpacing: 0 }}
            >
              {disambig.length} кандидат{ruEnd(disambig.length, ["", "а", "ов"])}
            </span>
          </div>
          <div>
            {disambig.map((d, i) => (
              <div
                key={i}
                className={"rf-disambig" + (d.is_chosen ? " is-chosen" : "")}
              >
                <div>
                  <div className="rf-disambig__name">{d.name}</div>
                  {d.sub && <div className="rf-disambig__sub">{d.sub}</div>}
                </div>
                <span
                  className={
                    "mt-badge mt-badge--no-dot" +
                    (d.is_chosen ? " mt-badge--success" : " mt-badge--neutral")
                  }
                >
                  {d.is_chosen ? "выбран" : "—"}
                </span>
                <div className="rf-disambig__bar">
                  <i style={{ width: `${Math.min(100, d.score * 100)}%` }} />
                </div>
                <span className="rf-disambig__score">{d.score.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <RawPayloadSection item={item} payload={payload} />
    </>
  );
}

function RawPayloadSection({
  item,
  payload,
}: {
  item: ReviewItem;
  payload: PayloadShape;
}) {
  const itemFields: Array<[string, unknown]> = [
    ["source_lang", item.source_lang],
    ["target_lang", item.target_lang],
    ["source_text", item.source_text],
    ["candidate_text", item.candidate_text],
    ["canonical_text", item.canonical_text],
    ["concept_key", item.concept_key],
    ["source_key", item.source_key],
    ["source_ref", item.source_ref],
    ["confidence", item.confidence],
    ["priority", item.priority],
  ];
  const filtered = itemFields.filter(
    ([, v]) => v != null && v !== "" && !(Array.isArray(v) && v.length === 0),
  );
  const payloadKeys = Object.keys(payload as Record<string, unknown>);
  return (
    <section className="rf-section">
      <details>
        <summary
          className="rf-section__head"
          style={{ cursor: "pointer", listStyle: "none", userSelect: "none" }}
        >
          <span>Все поля (техническая информация)</span>
          <span className="mt-toolbar__sep" style={{ flex: 1 }} />
          <span
            className="mt-text-tight mt-muted"
            style={{ textTransform: "none", letterSpacing: 0 }}
          >
            item · {filtered.length} · payload · {payloadKeys.length}
          </span>
        </summary>
        <div className="rf-section__body">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(160px, max-content) 1fr",
              gap: "4px 12px",
              fontSize: "var(--fs-sm)",
            }}
          >
            {filtered.map(([k, v]) => (
              <Row key={`item:${k}`} k={k} v={v} prefix="item." />
            ))}
            {payloadKeys.map((k) => (
              <Row
                key={`payload:${k}`}
                k={k}
                v={(payload as Record<string, unknown>)[k]}
                prefix="payload."
              />
            ))}
          </div>
        </div>
      </details>
    </section>
  );
}

function Row({ k, v, prefix }: { k: string; v: unknown; prefix: string }) {
  return (
    <>
      <div
        className="mt-mono mt-text-tight mt-muted"
        style={{ fontSize: "var(--fs-xs)" }}
      >
        {prefix}
        <span style={{ color: "var(--text-soft)" }}>{k}</span>
      </div>
      <div
        style={{
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }}
      >
        {renderInlineValue(v)}
      </div>
    </>
  );
}

function renderInlineValue(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v))
    return v.length === 0
      ? "[]"
      : v.length <= 5
        ? v.map(renderInlineValue).join(", ")
        : `${v.slice(0, 5).map(renderInlineValue).join(", ")} +${v.length - 5}`;
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function EvidenceTab({ payload }: { payload: PayloadShape }) {
  const examples = payload.examples ?? [];
  const evidence = payload.evidence ?? [];
  const refs = payload.source_refs ?? [];

  if (examples.length === 0 && evidence.length === 0 && refs.length === 0) {
    return <div className="mt-empty">Нет свидетельств в payload.</div>;
  }

  return (
    <>
      {examples.length > 0 && (
        <section className="rf-section">
          <div className="rf-section__head">
            <span>Использование в корпусе</span>
            <span className="mt-toolbar__sep" style={{ flex: 1 }} />
            <span
              className="mt-text-tight mt-muted"
              style={{ textTransform: "none", letterSpacing: 0 }}
            >
              {examples.length} пример{ruEnd(examples.length, ["", "а", "ов"])}
            </span>
          </div>
          <div className="rf-section__body">
            {examples.slice(0, 8).map((ex, i) => (
              <ExampleBlock key={i} value={ex} />
            ))}
          </div>
        </section>
      )}
      {evidence.length > 0 && (
        <section className="rf-section">
          <div className="rf-section__head">
            <span>Доказательства</span>
          </div>
          <div className="rf-section__body">
            {evidence.slice(0, 8).map((ev, i) => (
              <ExampleBlock key={i} value={ev} />
            ))}
          </div>
        </section>
      )}
      {refs.length > 0 && (
        <section className="rf-section">
          <div className="rf-section__head">
            <span>Ссылки на источники</span>
            <span className="mt-toolbar__sep" style={{ flex: 1 }} />
            <span
              className="mt-text-tight mt-muted"
              style={{ textTransform: "none", letterSpacing: 0 }}
            >
              {refs.length}
            </span>
          </div>
          <div className="rf-section__body">
            <div className="rf-ref-list">
              {refs.slice(0, 30).map((r) => (
                <a key={r} className="rf-ref" href="#" onClick={(e) => e.preventDefault()}>
                  {r} <span className="rf-ref__icon">↗</span>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function HistoryTab({ decisions }: { decisions: ReviewDecision[] }) {
  if (decisions.length === 0) {
    return <div className="mt-empty">Нет истории решений.</div>;
  }
  return (
    <section className="rf-section">
      <div className="rf-section__head">
        <span>История</span>
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
        <span className="mt-mono mt-text-tight">{decisions.length}</span>
      </div>
      <div className="rf-section__body" style={{ display: "grid", gap: 8 }}>
        {decisions.map((d) => (
          <div
            key={d.decision_id}
            style={{
              padding: "8px 10px",
              background: "var(--panel-soft)",
              borderRadius: "var(--r-sm)",
              fontSize: "var(--fs-sm)",
            }}
          >
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span
                className={
                  "mt-badge mt-badge--no-dot " +
                  badgeForDecision(d.decision)
                }
              >
                {decisionLabel(d.decision)}
              </span>
              <strong>{d.reviewer}</strong>
              {d.created_at && (
                <span
                  className="mt-mono mt-muted"
                  style={{ fontSize: "var(--fs-xs)" }}
                >
                  {new Date(d.created_at).toLocaleString("ru-RU")}
                </span>
              )}
            </div>
            {d.reviewer_note && (
              <div
                className="mt-text-soft"
                style={{ marginTop: 4, fontStyle: "italic" }}
              >
                «{d.reviewer_note}»
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function Timeline({
  decisions,
  username,
}: {
  decisions: ReviewDecision[];
  username: string;
}) {
  return (
    <div style={{ padding: "12px 14px" }}>
      <div className="mt-timeline">
        <div className="mt-timeline__item">
          <div className="mt-timeline__dot" />
          <div>
            <div className="mt-timeline__head">
              <span
                className="mt-timeline__verb"
                style={{ background: "var(--blue-50)", color: "var(--blue-500)" }}
              >
                открыт
              </span>
              <span className="mt-text-tight">{username}</span>
              <span className="mt-timeline__meta">· сейчас</span>
            </div>
            <div className="mt-text-tight mt-muted">открыт в режиме фокуса</div>
          </div>
        </div>
        {decisions.map((d) => (
          <div
            key={d.decision_id}
            className={"mt-timeline__item " + timelineClass(d.decision)}
          >
            <div className="mt-timeline__dot" />
            <div>
              <div className="mt-timeline__head">
                <span className="mt-timeline__verb">{decisionLabel(d.decision)}</span>
                <span className="mt-text-tight">{d.reviewer}</span>
                <span className="mt-timeline__meta">
                  · {formatRelative(d.created_at)}
                </span>
              </div>
              {d.reviewer_note && (
                <div className="mt-text-tight mt-text-soft">
                  «{d.reviewer_note}»
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Рендер одного примера из payload.examples / evidence — может быть строкой
 *  или объектом со множеством полей (sentence/text/source/url). */
function ExampleBlock({ value }: { value: string | Record<string, unknown> }) {
  if (typeof value === "string") {
    return <div className="rf-example">{value}</div>;
  }
  const v = value as Record<string, unknown>;
  const text = (v.text ?? v.context ?? v.snippet ?? v.fragment ?? v.sentence) as
    | string
    | undefined;
  const source = (v.source ?? v.source_name ?? v.url) as string | undefined;
  return (
    <div className="rf-example">
      {text ?? JSON.stringify(value)}
      {source && <span className="rf-example__src">{source}</span>}
    </div>
  );
}

function badgeForStatus(status: string): string {
  switch (status) {
    case "approved":
      return "mt-badge--success";
    case "rejected":
      return "mt-badge--danger";
    case "needs_review":
    case "needs_second_review":
      return "mt-badge--warning";
    default:
      return "mt-badge--neutral";
  }
}

function badgeForDecision(d: string): string {
  switch (d) {
    case "approve":
    case "edit_and_approve":
    case "apply_draft":
      return "mt-badge--success";
    case "reject":
      return "mt-badge--danger";
    case "needs_second_review":
    case "mark_ambiguous":
    case "needs_review":
      return "mt-badge--warning";
    default:
      return "mt-badge--neutral";
  }
}

function timelineClass(d: string): string {
  switch (d) {
    case "approve":
    case "edit_and_approve":
      return "mt-timeline__item--approve";
    case "reject":
      return "mt-timeline__item--reject";
    case "edit":
      return "mt-timeline__item--edit";
    default:
      return "";
  }
}

// fieldOverridesForItem вынесен в ./components/recordForms/shared.ts.

/** Из массива decisions берёт after_payload самого свежего edit_and_approve
 *  (или approve с непустым after_payload) — чтобы UI показывал правки,
 *  которые reviewer уже зафиксировал, поверх payload-snapshot из БД. */
function pickLatestAfterPayload(
  decisions: ReviewDecision[] | undefined,
): Record<string, unknown> {
  if (!decisions || decisions.length === 0) return {};
  // Сортировано по created_at DESC backend'ом, но на всякий случай
  // фильтруем по непустому after_payload.
  for (const d of decisions) {
    const ap = (d as unknown as { after_payload?: unknown }).after_payload;
    if (ap && typeof ap === "object" && Object.keys(ap as object).length > 0) {
      return ap as Record<string, unknown>;
    }
  }
  return {};
}

function focusUrl(item: { review_item_id?: string; stable_key: string }, params: URLSearchParams): string {
  const qs = params.toString();
  const key = item.review_item_id ?? item.stable_key;
  return `/admin/review/focus/${encodeURIComponent(key)}${qs ? "?" + qs : ""}`;
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  const m = Math.round(diff / 60_000);
  if (m < 1) return "только что";
  if (m < 60) return `${m} мин назад`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} ч назад`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} д назад`;
  return new Date(iso).toLocaleDateString("ru-RU");
}
